import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUp, Cloud, KeyRound, Mail, Phone, Wifi } from "lucide-react";
import MarketingLayout from "../marketing/MarketingLayout";
import SiteMeta from "../marketing/SiteMeta";
import { LEGAL_DOCS } from "./legalDocs";
import { MARKETING_ROUTES } from "../marketing/routes";
import "./legalPage.css";
import "./privacyPolicy.css";
import "./serviceDelivery.css";

const POLICY = LEGAL_DOCS.delivery;

const SNAPSHOT = [
  { icon: Cloud, label: "Delivery method", value: "Digital SaaS access through the Brixigo Platform" },
  { icon: KeyRound, label: "Activation", value: "Access after successful subscription and payment" },
  { icon: Wifi, label: "Customer setup", value: "Internet, device, and browser required to use the Services" },
  { icon: Phone, label: "Support", value: "support@brixigo.com · +91 83091 96410" },
];

function sectionAnchor(heading) {
  return `section-${String(heading).split(" ")[0].replace(/\./g, "")}`;
}

function splitHeading(heading) {
  const match = String(heading).match(/^(\d+)\.\s*(.*)$/);
  return match ? { num: match[1], title: match[2] } : { num: "", title: heading };
}

function PolicyText({ text }) {
  if (!text) return null;
  const parts = text.split(
    /(Terms of Use|Privacy Policy|Cancellation & Refund Policy|support@brixigo.com|\+91 83091 96410)/g
  );
  return (
    <>
      {parts.map((part, i) => {
        if (part === "Terms of Use") {
          return (
            <Link key={i} to={MARKETING_ROUTES.terms}>
              {part}
            </Link>
          );
        }
        if (part === "Privacy Policy") {
          return (
            <Link key={i} to={MARKETING_ROUTES.privacy}>
              {part}
            </Link>
          );
        }
        if (part === "Cancellation & Refund Policy") {
          return (
            <Link key={i} to={MARKETING_ROUTES.refund}>
              {part}
            </Link>
          );
        }
        if (part === "support@brixigo.com") {
          return (
            <a key={i} href="mailto:support@brixigo.com">
              support@brixigo.com
            </a>
          );
        }
        if (part === "+91 83091 96410") {
          return (
            <a key={i} href="tel:+918309196410">
              +91 83091 96410
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function Blocks({ paragraphs = [], list }) {
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={`p-${i}`}>
          <PolicyText text={p} />
        </p>
      ))}
      {list?.length ? (
        <ul className="legal-list">
          {list.map((item) => (
            <li key={item}>
              <PolicyText text={item} />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function ContactBlock({ contact }) {
  if (!contact) return null;
  return (
    <address className="pp-contact legal-contact">
      {contact.name ? <strong>{contact.name}</strong> : null}
      {contact.emailLabel ? <p className="pp-contact-label">{contact.emailLabel}</p> : null}
      {contact.email ? (
        <p>
          Email: <a href={`mailto:${contact.email}`}>{contact.email}</a>
        </p>
      ) : null}
      {contact.phone ? (
        <p>
          Phone: <a href={contact.phoneHref || `tel:${contact.phone}`}>{contact.phone}</a>
        </p>
      ) : null}
      {contact.address ? (
        <p>
          {contact.addressLabel ? `${contact.addressLabel}: ` : "Address: "}
          {contact.address}
        </p>
      ) : null}
    </address>
  );
}

export default function ServiceDeliveryPolicyPage() {
  const location = useLocation();
  const tocListRef = useRef(null);
  const visibleIds = useRef(new Set());
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState(sectionAnchor(POLICY.sections[0].heading));
  const [query, setQuery] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const sectionIds = useMemo(
    () => POLICY.sections.map((section) => sectionAnchor(section.heading)),
    []
  );

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return POLICY.sections;
    return POLICY.sections.filter((section) => section.heading.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
      setShowTop(window.scrollY > 480);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const nodes = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleIds.current.add(entry.target.id);
          else visibleIds.current.delete(entry.target.id);
        });
        const next = sectionIds.find((id) => visibleIds.current.has(id));
        if (next) setActiveId(next);
      },
      { rootMargin: "-120px 0px -58% 0px", threshold: 0 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sectionIds]);

  useEffect(() => {
    const link = tocListRef.current?.querySelector(`[data-id="${activeId}"]`);
    link?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (!hash) return;
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const scrollToSection = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveId(id);
    setTocOpen(false);
  };

  return (
    <MarketingLayout className="legal-page-shell pp-page sdp-page">
      <SiteMeta title={POLICY.pageTitle} description={POLICY.description} />

      <div className="pp-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      <header className="pp-hero">
        <div className="pp-hero-inner">
          <p className="pp-kicker">Legal</p>
          <h1>{POLICY.title}</h1>
          <p className="pp-dates">
            <span>Effective Date: {POLICY.effectiveDate}</span>
            <span>Last Updated: {POLICY.updated}</span>
          </p>
          {POLICY.intro.map((p, i) => (
            <p key={p.slice(0, 48)} className={i === 0 ? "pp-lead" : i === 1 ? "pp-callout" : "pp-intro"}>
              <PolicyText text={p} />
            </p>
          ))}
          <div className="pp-facts">
            {SNAPSHOT.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.label} className="pp-fact">
                  <div className="pp-fact-icon">
                    <Icon size={16} strokeWidth={2.2} />
                  </div>
                  <p>
                    {item.label}
                    <strong>{item.value}</strong>
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </header>

      <div className="pp-layout">
        <nav className={`pp-toc${tocOpen ? " is-open" : ""}`} aria-label="Service Delivery Policy sections">
          <div className="pp-toc-head">
            <p className="pp-toc-title">Contents</p>
            <button
              type="button"
              className="pp-toc-toggle"
              onClick={() => setTocOpen((open) => !open)}
              aria-expanded={tocOpen}
            >
              {tocOpen ? "Hide" : "Jump to"}
            </button>
          </div>
          <input
            className="pp-toc-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a section"
            aria-label="Find a policy section"
          />
          <ol ref={tocListRef}>
            {filteredSections.map((section) => {
              const id = sectionAnchor(section.heading);
              const { num, title } = splitHeading(section.heading);
              return (
                <li key={section.heading}>
                  <a
                    href={`#${id}`}
                    data-id={id}
                    className={activeId === id ? "is-active" : ""}
                    onClick={(event) => scrollToSection(event, id)}
                  >
                    {num ? <span className="pp-toc-num">{num}</span> : null}
                    <span>{title}</span>
                  </a>
                </li>
              );
            })}
          </ol>
          {!filteredSections.length ? <p className="pp-toc-empty">No matching sections.</p> : null}
        </nav>

        <article className="legal-page pp-article">
          {POLICY.sections.map((section) => (
            <section
              key={section.heading}
              id={sectionAnchor(section.heading)}
              className="legal-section"
            >
              <h2>{section.heading}</h2>
              <Blocks paragraphs={section.paragraphs} list={section.list} />
              {section.subsections?.map((sub) => (
                <div key={sub.heading} id={sectionAnchor(sub.heading)} className="legal-subsection">
                  <h3>{sub.heading}</h3>
                  <Blocks paragraphs={sub.paragraphs} list={sub.list} />
                </div>
              ))}
              <ContactBlock contact={section.contact} />
              <Blocks paragraphs={section.afterParagraphs} />
            </section>
          ))}

          {POLICY.note ? (
            <aside className="sdp-note" role="note">
              <p>
                <strong>Note:</strong> {POLICY.note}
              </p>
            </aside>
          ) : null}

          <p className="pp-footer-nav legal-back">
            <Link to={MARKETING_ROUTES.contact}>Contact Us</Link>
            <Link to={MARKETING_ROUTES.terms}>Terms of Use</Link>
            <a href="mailto:support@brixigo.com">
              <Mail size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              support@brixigo.com
            </a>
          </p>
        </article>
      </div>

      <button
        type="button"
        className={`pp-top${showTop ? " is-visible" : ""}`}
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowUp size={18} />
      </button>
    </MarketingLayout>
  );
}
